import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Check, CheckCircle, Minus, Plus, Search, X, ArrowRight, Lock, ChevronLeft } from 'lucide-react'
import { fetchPaidQuantities } from '../../../api/paymentsApi'
import PaymentSuccessScreen from './PaymentSuccessScreen'
import PrimaryLogo from '../../../assets/fonts/Primary logo.png'

// Payment modes
enum PaymentMode {
  FULL = 'full',
  SELECT_ITEMS = 'select',
  SPLIT_EQUALLY = 'split',
  FIXED_AMOUNT = 'fixed'
}

interface PaymentSectionProps {
  mesa: {
    mesaId: string
    restaurantId?: string
    numeroMesa: string
    items: Array<{
      id: string
      name: string
      price: number
      quantity: number
    }>
    montoTotal: number
    montoPagado: number
    restante: number
    moneda: string
    ticketId?: string
    /** Total with tax (for bill display). From API or computed from montoTotal + tax. */
    montoTotalConImpuestos?: number
    /** Restaurant tax rate (e.g. 18). Used when impuestosIncluded is false. */
    taxRate?: number
    /** If true, POS/montoTotal already includes tax; if false, YAP adds tax. */
    impuestosIncluded?: boolean
    /** Session/mesa estado ('abierta' | 'cerrada') when provided by backend. */
    estado?: string
  }
  /** When set, open on this tab (e.g. 'split' when user chose Split bill on landing). */
  initialPaymentMode?: 'full' | 'select' | 'split' | 'fixed'
  /** When set, back icon is shown in header (e.g. return to landing). */
  onBack?: () => void
}

// Format currency helper with thousand separators
const formatCurrency = (amount: number, moneda: string = 'DOP') => {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
  return `$${formatted} ${moneda}`
}

// Header Component — optional large variant and back icon for "Pagar cuenta completa"
const Header: React.FC<{ title: string; large?: boolean; onBack?: () => void }> = ({ title, large, onBack }) => {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-50/80 border-b border-gray-200/50">
      <div className={`${large ? 'w-full' : 'max-w-md'} mx-auto ${large ? 'px-4' : 'px-4'} flex items-center gap-3 ${large ? 'py-5 min-h-[4.5rem]' : 'h-12'} ${large && onBack ? 'justify-start' : large ? 'justify-start' : 'justify-center'}`}>
        {large && onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center w-10 h-10 -ml-2 rounded-full text-gray-600 hover:bg-gray-200/80 hover:text-gray-900 active:scale-95 transition-all"
            aria-label="Volver"
          >
            <ChevronLeft size={24} strokeWidth={2} />
          </button>
        )}
        <h1 className={`${large ? 'text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight' : 'text-sm font-semibold text-gray-900 tracking-tight'} ${large && onBack ? 'flex-1' : 'w-full'}`}>
          {title}
        </h1>
      </div>
    </header>
  )
}

// TabSelector Component
const TabSelector: React.FC<{
  currentMode: PaymentMode
  onModeChange: (mode: PaymentMode) => void
}> = ({ currentMode, onModeChange }) => {
  return (
    <div className="bg-gray-200/60 p-0.5 rounded-[1rem] flex gap-0.5 relative z-0">
      {[
        { mode: PaymentMode.FULL, label: 'Cuenta Completa' },
        { mode: PaymentMode.SELECT_ITEMS, label: 'Seleccionar Items' },
        { mode: PaymentMode.SPLIT_EQUALLY, label: 'Dividir Igual' },
      ].map((tab) => (
        <button
          key={tab.mode}
          onClick={() => onModeChange(tab.mode)}
          className={`flex-1 py-2 rounded-[0.875rem] text-[11px] font-semibold tracking-tight transition-all duration-300 relative z-10 ${
            currentMode === tab.mode
              ? 'bg-white text-gray-900 shadow-[0_1px_4px_rgba(0,0,0,0.06)]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

// SearchBar Component
const SearchBar: React.FC<{
  value: string
  onChange: (val: string) => void
}> = ({ value, onChange }) => {
  return (
    <div className="px-3 py-2 bg-white">
      <div className="relative group">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={14} />
        <input
          type="text"
          placeholder="Filtrar items..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-gray-100/70 text-gray-900 text-[13px] rounded-xl pl-9 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:bg-white transition-all placeholder-gray-400 font-medium"
        />
        {value && (
          <button
            onClick={() => onChange('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-gray-200/50 p-0.5 rounded-full transition-colors"
          >
            <X size={10} strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  )
}

// ItemRow Component — shows subtotal; tax line only when showItemTax (hidden for full-bill view)
const ItemRow: React.FC<{
  item: { id: string; name: string; price: number; quantity: number }
  isSelected: boolean
  mode: PaymentMode
  onToggle: (id: string) => void
  isPaid: boolean
  moneda: string
  taxRate?: number
  impuestosIncluded?: boolean
  showItemTax?: boolean
  spacious?: boolean
}> = ({ item, isSelected, mode, onToggle, isPaid, moneda, taxRate = 18, impuestosIncluded = true, showItemTax = true, spacious = false }) => {
  const isSelectMode = mode === PaymentMode.SELECT_ITEMS
  const subtotal = item.price * item.quantity
  const taxAmount = impuestosIncluded ? 0 : subtotal * (taxRate / 100)
  const unitPrice = item.price

  return (
    <div
      onClick={() => isSelectMode && !isPaid && onToggle(item.id)}
      className={`group flex flex-col transition-all duration-200 relative overflow-hidden
        ${spacious ? 'px-5 py-5' : 'px-3 py-2.5'}
        ${isSelectMode && !isPaid ? 'cursor-pointer active:scale-[0.99]' : 'cursor-default'}
        ${isSelectMode && isSelected ? 'bg-blue-50/50' : 'bg-transparent'}
        ${isPaid ? 'opacity-60' : ''}
      `}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {!isSelectMode && (
            <div className="w-0 -ml-2 overflow-hidden shrink-0" />
          )}
          <div className="flex flex-col min-w-0">
              <span
                className={`inline-flex items-center gap-1 font-medium leading-tight ${
                  spacious ? 'text-base text-gray-900' : 'text-[13px]'
                } ${isSelected && isSelectMode && !isPaid ? 'text-gray-900' : 'text-gray-700'}
                `}
              >
                <span className="truncate">{item.name}</span>
                {isPaid && (
                  <>
                    <CheckCircle className="text-green-600 w-3.5 h-3.5" />
                    <span className="text-[10px] text-green-600 font-medium">Pagado</span>
                  </>
                )}
              </span>
              {item.quantity > 1 && (
                <span className={`text-gray-400 font-medium mt-0.5 ${spacious ? 'text-sm' : 'text-[10px]'}`}>
                  {formatCurrency(unitPrice, moneda)} cada uno
                </span>
              )}
          </div>
        </div>
        <div className={`flex items-center gap-2 shrink-0 ${
          spacious ? 'text-base' : 'text-[13px]'
        }`}>
          <span className={`font-semibold text-gray-900 tabular-nums ${
            isSelected && isSelectMode && !isPaid ? 'text-blue-600' : ''
          }`}>
            {formatCurrency(subtotal, moneda)}
          </span>
          {isSelectMode && !isPaid && (
            <div className="relative w-8 h-8 flex items-center justify-center">
              <div
                className={`absolute inset-0 flex items-center justify-center rounded-full border-2 transition-all duration-200 ${
                  isSelected ? 'bg-gray-900 border-gray-900 scale-100 opacity-100' : 'border-gray-300 bg-transparent scale-100 opacity-0'
                }`}
                aria-hidden
              >
                <Check size={14} className="text-white" strokeWidth={3} />
              </div>
              <div
                className={`absolute inset-0 flex items-center justify-center rounded-full border-2 border-gray-300 text-gray-500 transition-all duration-200 ${
                  !isSelected ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                }`}
                aria-hidden
              >
                <Plus size={14} strokeWidth={2.5} />
              </div>
            </div>
          )}
        </div>
      </div>
      {showItemTax && !impuestosIncluded && taxAmount > 0 && (
        <div className="flex items-center justify-between mt-0.5">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`shrink-0 ${isSelectMode ? 'w-0' : 'w-0 -ml-2 overflow-hidden'}`} />
            <span className="text-[9px] text-gray-400">Impuesto ({taxRate}%)</span>
          </div>
          <span className="text-[9px] text-gray-400 tabular-nums shrink-0">
            {formatCurrency(taxAmount, moneda)}
          </span>
        </div>
      )}
    </div>
  )
}

// SummarySection Component — shows Subtotal, Impuesto (separate), then Total
const SummarySection: React.FC<{
  subtotal: number
  taxAmount: number
  total: number
  paid: number
  remaining: number
  selectedSubtotal?: number
  selectedTaxAmount?: number
  selectedTotal?: number
  showSelectionTotal: boolean
  moneda: string
  showTaxBreakdown: boolean
  hideRestante?: boolean
  totalLabelLeft?: boolean
  largeText?: boolean
}> = ({
  subtotal,
  taxAmount,
  total,
  paid,
  remaining,
  selectedSubtotal = 0,
  selectedTaxAmount = 0,
  selectedTotal = 0,
  showSelectionTotal,
  moneda,
  showTaxBreakdown,
  hideRestante = false,
  totalLabelLeft = false,
  largeText = false,
}) => {
  const labelCls = largeText ? 'text-sm' : 'text-[10px]'
  const valueCls = largeText ? 'text-base' : 'text-[10px]'
  const totalLabelCls = largeText ? 'text-sm font-medium text-gray-400' : 'text-[10px] font-medium text-gray-400'
  const totalValueCls = largeText ? 'text-lg font-bold text-gray-900 tracking-tight' : 'text-lg font-bold text-gray-900 tracking-tight'
  const impuestoCls = largeText ? 'text-[9px]' : 'text-[9px]'

  const cardCls = largeText ? 'bg-white rounded-[20px] p-5 shadow-sm' : 'bg-white rounded-xl shadow-sm border border-gray-100/50 p-3'

  return (
    <div className={`${cardCls} relative overflow-hidden`}>
      <div className="relative z-10 space-y-2">
        {showSelectionTotal ? (
          <div className="flex flex-col gap-1">
            {showTaxBreakdown && selectedTaxAmount > 0 ? (
              <>
                <div className={`flex justify-between ${labelCls}`}>
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-gray-700">{formatCurrency(selectedSubtotal, moneda)}</span>
                </div>
                <div className={`flex justify-between ${impuestoCls}`}>
                  <span className="text-gray-400">Impuesto</span>
                  <span className="text-gray-400">{formatCurrency(selectedTaxAmount, moneda)}</span>
                </div>
              </>
            ) : null}
            <div className="flex flex-col items-center justify-center">
              <span className={`${totalLabelCls} mb-0.5`}>Total</span>
              <span className={totalValueCls}>
                {formatCurrency(selectedTotal, moneda)}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {largeText && showTaxBreakdown && taxAmount > 0 ? (
              <>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[15px] text-[#8E8E93]">Subtotal</span>
                  <span className="text-[15px] text-[#8E8E93]">{formatCurrency(subtotal, moneda)}</span>
                </div>
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[12px] text-[#C7C7CC]">Impuesto</span>
                  <span className="text-[12px] text-[#C7C7CC]">{formatCurrency(taxAmount, moneda)}</span>
                </div>
              </>
            ) : !largeText && showTaxBreakdown && taxAmount > 0 ? (
              <>
                <div className={`flex justify-between ${labelCls}`}>
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-gray-700">{formatCurrency(subtotal, moneda)}</span>
                </div>
                <div className={`flex justify-between ${impuestoCls}`}>
                  <span className="text-gray-400">Impuesto</span>
                  <span className="text-gray-400">{formatCurrency(taxAmount, moneda)}</span>
                </div>
              </>
            ) : null}
            <div className={totalLabelLeft ? 'flex justify-between items-center gap-2' : 'flex flex-col items-center justify-center'}>
              {largeText ? (
                <>
                  <span className="text-[16px] text-[#8E8E93]">Total de la cuenta</span>
                  <span className="text-[18px] font-bold text-black">{formatCurrency(total, moneda)}</span>
                </>
              ) : (
                <>
                  <span className={totalLabelLeft ? totalLabelCls : totalLabelCls + ' mb-0.5'}>Total de la cuenta</span>
                  <span className={totalValueCls}>{formatCurrency(total, moneda)}</span>
                </>
              )}
            </div>
          </div>
        )}

        {paid > 0 && (
          <div className={`flex justify-between items-center bg-green-50/50 rounded-lg border border-green-100/50 ${largeText ? 'p-2' : 'p-1.5'}`}>
            <span className={`text-green-700 font-medium flex items-center gap-1 ${valueCls}`}>
              <Check size={10} className="bg-green-200 text-green-700 rounded-full p-0.5" />
              Pagado
            </span>
            <span className={`font-semibold text-green-700 ${valueCls}`}>{formatCurrency(paid, moneda)}</span>
          </div>
        )}

        {!hideRestante && (
          <div className={`flex justify-between items-center pt-1 border-t border-gray-100 ${labelCls}`}>
            <span className="text-gray-400">Restante</span>
            <span className="text-gray-900 font-semibold">{formatCurrency(remaining, moneda)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// SplitEqualControl Component — circle with segment, mesa, pagando por / total en la mesa, restante, estás pagando
const SplitEqualControl: React.FC<{
  totalAmount: number
  splitCount: number
  onCountChange: (count: number) => void
  totalAtTable: number
  onTotalAtTableChange: (count: number) => void
  numeroMesa: string
  moneda: string
}> = ({ totalAmount, splitCount, onCountChange, totalAtTable, onTotalAtTableChange, numeroMesa, moneda }) => {
  // totalAmount is what's still owed for the whole table.
  // splitCount = how many people you're paying for.
  // totalAtTable = total people at the table.
  const amountPerPerson = totalAtTable > 0 ? totalAmount / totalAtTable : 0
  const amountToPay = amountPerPerson * splitCount
  const circumference = 2 * Math.PI * 72
  const filledFraction = totalAtTable > 0 ? splitCount / totalAtTable : 0
  const strokeDash = filledFraction * circumference
  const strokeGap = circumference - strokeDash

  const circleBtnCls = 'flex items-center justify-center bg-white rounded-full text-gray-800 hover:bg-gray-100 active:scale-95 transition-all flex-shrink-0 w-[28px] h-[28px] min-w-[28px] min-h-[28px] aspect-square'
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100/50 p-5 mb-3 flex flex-col">
      {/* Circle and "mesa" — both centred */}
      <div className="flex justify-center mb-6">
        <div className="relative w-[180px] h-[180px] flex items-center justify-center">
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 180 180">
            <circle
              cx="90"
              cy="90"
              r="72"
              fill="none"
              stroke="#E5E5EA"
              strokeWidth="12"
            />
            <circle
              cx="90"
              cy="90"
              r="72"
              fill="none"
              stroke="#34C759"
              strokeWidth="12"
              strokeDasharray={`${strokeDash} ${strokeGap}`}
              strokeLinecap="round"
              className="transition-all duration-300"
            />
          </svg>
          <span className="relative z-10 text-[15px] font-semibold text-gray-800 text-center">mesa {numeroMesa}</span>
        </div>
      </div>

      {/* Stepper rows — grid so black pill [ - X + ] is dead center; text in equal side columns */}
      <div className="w-full flex flex-col gap-3 mb-5">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center w-full">
          <span className="text-right text-[#8E8E93] text-[13px]">pagando por</span>
          <div className="bg-black rounded-full h-9 inline-flex items-center justify-between gap-0 px-1.5 w-[110px] flex-shrink-0">
            <button type="button" onClick={() => onCountChange(Math.max(1, splitCount - 1))} className={circleBtnCls} aria-label="Menos">
              <Minus size={14} strokeWidth={2.5} />
            </button>
            <div className="flex-1 flex items-center justify-center min-w-0">
              <span className="text-sm font-bold text-white tabular-nums">{splitCount}</span>
            </div>
            <button type="button" onClick={() => onCountChange(Math.min(totalAtTable, splitCount + 1))} className={circleBtnCls} aria-label="Más">
              <Plus size={14} strokeWidth={2.5} />
            </button>
          </div>
          <span className="text-left text-[#8E8E93] text-[13px]">personas</span>
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center w-full">
          <span className="text-right text-[#8E8E93] text-[13px]">total de</span>
          <div className="bg-black rounded-full h-9 inline-flex items-center justify-between gap-0 px-1.5 w-[110px] flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                const next = Math.max(1, totalAtTable - 1)
                onTotalAtTableChange(next)
                if (splitCount > next) onCountChange(next)
              }}
              className={circleBtnCls}
              aria-label="Menos"
            >
              <Minus size={14} strokeWidth={2.5} />
            </button>
            <div className="flex-1 flex items-center justify-center min-w-0">
              <span className="text-sm font-bold text-white tabular-nums">{totalAtTable}</span>
            </div>
            <button type="button" onClick={() => onTotalAtTableChange(totalAtTable + 1)} className={circleBtnCls} aria-label="Más">
              <Plus size={14} strokeWidth={2.5} />
            </button>
          </div>
          <span className="text-left text-[#8E8E93] text-[13px]">personas en la mesa</span>
        </div>
      </div>

      {/* Left-to-right block: restante a pagar / estás pagando (not centred) */}
      <div className="w-full border-t border-gray-100 pt-3">
        <div className="flex justify-between items-center mb-2">
          <span className="text-[13px] text-gray-500">restante a pagar</span>
          <span className="text-[15px] font-medium text-gray-800">{formatCurrency(totalAmount, moneda)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[15px] font-medium text-gray-700">estás pagando</span>
          <span className="text-xl font-bold text-gray-900 tracking-tight">{formatCurrency(amountToPay, moneda)}</span>
        </div>
      </div>
    </div>
  )
}

// Manual fixed amount control — visually matches the Split equally screen wrapper
const ManualFixedAmountControl: React.FC<{
  remainingAmount: number
  fixedAmount: number
  onFixedAmountChange: (amount: number) => void
  numeroMesa: string
  moneda: string
}> = ({ remainingAmount, fixedAmount, onFixedAmountChange, numeroMesa: _numeroMesa, moneda }) => {
  const safeRemaining = Math.max(0, remainingAmount)
  const safeFixed = Math.min(Math.max(0, fixedAmount), safeRemaining)
  const safeRemainingCents = Math.round(safeRemaining * 100)

  const [digits, setDigits] = useState<string>(() => {
    const cents = Math.round(Math.max(0, safeFixed) * 100)
    return cents > 0 ? String(cents) : '0'
  })

  const formatDigitsCurrency = (value: number) => {
    return value.toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  const handleKeyPress = (key: string) => {
    if (safeRemainingCents <= 0) return

    if (key === 'backspace') {
      setDigits((prev) => (prev.length > 1 ? prev.slice(0, -1) : '0'))
      return
    }

    setDigits((prev) => {
      if (!/^\d$/.test(key)) return prev

      if (prev === '0') {
        const next = key
        const nextCents = parseInt(next, 10)
        return nextCents > safeRemainingCents ? String(safeRemainingCents) : next
      }

      if (prev.length >= 8) return prev

      const next = prev + key
      const nextCents = parseInt(next, 10)
      if (nextCents > safeRemainingCents) return String(safeRemainingCents)
      return next
    })
  }

  const numericValue = parseInt(digits, 10) / 100

  useEffect(() => {
    const clamped = Math.min(Math.max(numericValue, 0), safeRemaining)
    onFixedAmountChange(clamped)
  }, [digits, numericValue, safeRemaining, onFixedAmountChange])

  const KEYPAD_BUTTONS = [
    { num: '1', letters: '' },
    { num: '2', letters: 'A B C' },
    { num: '3', letters: 'D E F' },
    { num: '4', letters: 'G H I' },
    { num: '5', letters: 'J K L' },
    { num: '6', letters: 'M N O' },
    { num: '7', letters: 'P Q R S' },
    { num: '8', letters: 'T U V' },
    { num: '9', letters: 'W X Y Z' },
  ]

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100/50 p-5 mb-3 flex flex-col">
      {/* Input Area */}
      <div className="flex justify-center items-center text-5xl sm:text-6xl font-semibold text-gray-900 mb-12">
        <span>RD$ {formatDigitsCurrency(numericValue)}</span>
        <div className="w-0.5 h-12 sm:h-14 bg-blue-500 ml-1 animate-pulse" />
      </div>

      {/* Keypad */}
      <div className="flex-1">
        <div className="grid grid-cols-3 gap-y-4 gap-x-4">
          {KEYPAD_BUTTONS.map(({ num, letters }) => (
            <button
              key={num}
              type="button"
              onClick={() => handleKeyPress(num)}
              disabled={safeRemainingCents <= 0}
              className="flex flex-col items-center justify-center py-3 active:bg-gray-100 rounded-2xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="text-3xl font-medium text-gray-900 leading-none">{num}</span>
              <span className="text-[10px] font-bold text-gray-400 mt-1 tracking-widest h-3">{letters}</span>
            </button>
          ))}

          <div />

          <button
            type="button"
            onClick={() => handleKeyPress('0')}
            disabled={safeRemainingCents <= 0}
            className="flex flex-col items-center justify-center py-3 active:bg-gray-100 rounded-2xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="text-3xl font-medium text-gray-900 leading-none">0</span>
            <span className="text-[10px] font-bold text-gray-400 mt-1 tracking-widest h-3" />
          </button>

          <button
            type="button"
            onClick={() => handleKeyPress('backspace')}
            disabled={safeRemainingCents <= 0}
            className="flex items-center justify-center py-3 active:bg-gray-100 rounded-2xl transition-colors text-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"></path>
              <line x1="18" y1="9" x2="12" y2="15"></line>
              <line x1="12" y1="9" x2="18" y2="15"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Left-to-right restante + estas pagando */}
      <div className="w-full border-t border-gray-100 pt-3 mt-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-[13px] text-gray-500">restante a pagar</span>
          <span className="text-[15px] font-medium text-gray-800">{formatCurrency(safeRemaining, moneda)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[15px] font-medium text-gray-700">estás pagando</span>
          <span className="text-xl font-bold text-gray-900 tracking-tight">{formatCurrency(safeFixed, moneda)}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * Enhanced Payment Section with exact design match
 * Supports: Full bill, Select items, Split equally
 */
const modeFromProp = (p?: 'full' | 'select' | 'split' | 'fixed'): PaymentMode => {
  if (p === 'split') return PaymentMode.SPLIT_EQUALLY
  if (p === 'select') return PaymentMode.SELECT_ITEMS
  if (p === 'fixed') return PaymentMode.FIXED_AMOUNT
  return PaymentMode.FULL
}

export function PaymentSection({ mesa, initialPaymentMode, onBack }: PaymentSectionProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const navState = (location.state || {}) as any

  const initialTotalAtTableFromNav =
    typeof navState?.totalAtTable === 'number' ? navState.totalAtTable : 4
  const initialSplitCountFromNav =
    typeof navState?.splitCount === 'number' ? navState.splitCount : 2

  const initialTotalAtTable = Math.max(1, Math.floor(initialTotalAtTableFromNav))
  const initialSplitCount = Math.min(
    Math.max(1, Math.floor(initialSplitCountFromNav)),
    initialTotalAtTable
  )

  const initialFixedFromNav =
    typeof navState?.fixedAmount === 'number' ? navState.fixedAmount : undefined
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(() => modeFromProp(initialPaymentMode))
  const hasAutoRedirectedRef = useRef(false)
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set())
  const [splitCount, setSplitCount] = useState<number>(() => initialSplitCount)
  const [totalAtTable, setTotalAtTable] = useState<number>(() => initialTotalAtTable)
  const [isProcessing, setIsProcessing] = useState(false)
  const [paidQuantities, setPaidQuantities] = useState<Map<string, number>>(new Map())
  const [searchQuery, setSearchQuery] = useState('')

  // Load paid quantities
  React.useEffect(() => {
    const ticketId = mesa.ticketId || mesa.mesaId
    if (ticketId) {
      fetchPaidQuantities(ticketId)
        .then(setPaidQuantities)
        .catch(err => {
          console.error('Failed to fetch paid quantities:', err)
        })
    }
  }, [mesa.ticketId, mesa.mesaId])

  // Check if item is fully paid
  const isItemPaid = (item: { id: string; quantity: number }) => {
    const paidQty = paidQuantities.get(item.id) || 0
    return paidQty >= item.quantity
  }

  // Filter out paid items for selection
  const availableItems = useMemo(() => {
    return mesa.items.filter(item => !isItemPaid(item))
  }, [mesa.items, paidQuantities])

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery) return mesa.items
    return mesa.items.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [mesa.items, searchQuery])

  // Tax settings from API (bill and items show totals with tax when impuestosIncluded is false)
  const taxRate = mesa.taxRate ?? 18
  const impuestosIncluded = mesa.impuestosIncluded === true
  const totalAmount =
    mesa.montoTotalConImpuestos ??
    (impuestosIncluded ? (mesa.montoTotal ?? 0) : (mesa.montoTotal ?? 0) * (1 + taxRate / 100))
  const paidAmount = mesa.montoPagado ?? 0
  const remainingAmount = Math.max(0, totalAmount - paidAmount)
  const isClosed = (mesa as any)?.estado === 'cerrada'
  const moneda = mesa.moneda || 'DOP'
  const isManualMesa =
    (mesa as any)?.integrationMode === 'manual' || (mesa as any)?.activeSource === 'manual'
  const [fixedAmount, setFixedAmount] = useState<number>(() => {
    const candidate =
      typeof initialFixedFromNav === 'number' && Number.isFinite(initialFixedFromNav) && initialFixedFromNav > 0
        ? initialFixedFromNav
        : remainingAmount
    return Math.min(candidate, remainingAmount)
  })

  useEffect(() => {
    if (paymentMode !== PaymentMode.FIXED_AMOUNT) return
    setFixedAmount((prev) => {
      const nextPrev = Number.isFinite(prev) && prev > 0 ? prev : remainingAmount
      return Math.min(nextPrev, remainingAmount)
    })
  }, [paymentMode, remainingAmount])

  const billSubtotal = mesa.montoTotal ?? 0
  const billTaxAmount = impuestosIncluded ? 0 : Math.round((totalAmount - billSubtotal) * 100) / 100

  const { selectedSubtotal, selectedTaxAmount, currentPayableAmount } = useMemo(() => {
    const selSub = availableItems
      .filter(item => selectedItemIds.has(item.id))
      .reduce((sum, item) => sum + item.price * item.quantity, 0)
    const selTax = impuestosIncluded ? 0 : Math.round(selSub * (taxRate / 100) * 100) / 100
    const payable = impuestosIncluded ? selSub : selSub + selTax
    return {
      selectedSubtotal: selSub,
      selectedTaxAmount: selTax,
      currentPayableAmount:
        paymentMode === PaymentMode.FULL
          ? remainingAmount
          : paymentMode === PaymentMode.SPLIT_EQUALLY
            ? (remainingAmount * splitCount) / Math.max(1, totalAtTable)
            : paymentMode === PaymentMode.FIXED_AMOUNT
              ? fixedAmount
            : paymentMode === PaymentMode.SELECT_ITEMS
              ? payable
              : 0,
    }
  }, [
    paymentMode,
    remainingAmount,
    splitCount,
    totalAtTable,
    fixedAmount,
    availableItems,
    selectedItemIds,
    impuestosIncluded,
    taxRate,
  ])

  // Manual mode UX: if user chose "Pagar cuenta completa" (FULL) there are no items to select,
  // so we should go straight to tip selection.
  useEffect(() => {
    if (!isManualMesa) return
    if (hasAutoRedirectedRef.current) return
    if (initialPaymentMode !== 'full') return
    if (paymentMode !== PaymentMode.FULL) return
    if (currentPayableAmount <= 0) return

    hasAutoRedirectedRef.current = true

    const ticketId = mesa.ticketId || mesa.mesaId
    navigate('/tip-selection', {
      state: {
        ticketId,
        mesaId: mesa.mesaId,
        restaurantId: mesa.restaurantId,
        waiterName: mesa.numeroMesa || 'tu mesero',
        staffId: (mesa as any).staffId,
        amount: currentPayableAmount,
        selectedItems: [],
        splitCount: undefined,
        paymentMode,
        moneda,
      },
      replace: true,
    })
  }, [
    isManualMesa,
    initialPaymentMode,
    paymentMode,
    currentPayableAmount,
    mesa.ticketId,
    mesa.mesaId,
    mesa.restaurantId,
    mesa.numeroMesa,
    moneda,
    navigate,
  ])

  const handleToggleItem = (id: string) => {
    const newSet = new Set(selectedItemIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedItemIds(newSet)
  }

  const handlePayment = async () => {
    if (currentPayableAmount <= 0) return
    
    setIsProcessing(true)

    try {
      const ticketId = mesa.ticketId || mesa.mesaId

      const isManualMesa =
        (mesa as any)?.integrationMode === 'manual' ||
        (mesa as any)?.activeSource === 'manual'

      // Manual mode: mesa has no POS items, so we pay using the computed payable amount only.
      if (isManualMesa) {
        navigate('/tip-selection', {
          state: {
            ticketId,
            mesaId: mesa.mesaId,
            restaurantId: mesa.restaurantId,
            waiterName: mesa.numeroMesa || 'tu mesero',
            staffId: (mesa as any).staffId,
            amount: currentPayableAmount,
            selectedItems: [],
            splitCount: paymentMode === PaymentMode.SPLIT_EQUALLY ? splitCount : undefined,
            totalAtTable: paymentMode === PaymentMode.SPLIT_EQUALLY ? totalAtTable : undefined,
            fixedAmount: paymentMode === PaymentMode.FIXED_AMOUNT ? fixedAmount : undefined,
            paymentMode,
          },
        })
        return
      }
      
      // Build selected items based on mode
      let itemsToPay: Array<{ itemId: string; quantity: number }> = []
      
      if (paymentMode === PaymentMode.FULL) {
        itemsToPay = availableItems.map(item => ({
          itemId: item.id,
          quantity: item.quantity
        }))
      } else if (paymentMode === PaymentMode.SELECT_ITEMS) {
        itemsToPay = availableItems
          .filter(item => selectedItemIds.has(item.id))
          .map(item => ({
            itemId: item.id,
            quantity: item.quantity
          }))
      } else if (paymentMode === PaymentMode.SPLIT_EQUALLY) {
        itemsToPay = availableItems.map(item => ({
          itemId: item.id,
          quantity: item.quantity
        }))
      }

      if (itemsToPay.length === 0) {
        alert('No hay items seleccionados para pagar')
        setIsProcessing(false)
        return
      }

      // Amount to charge: with tax when restaurant has impuestosIncluded false
      const subtotal = itemsToPay.reduce((sum, itemRef) => {
        const item = mesa.items.find(i => i.id === itemRef.itemId)
        const line = item ? item.price * item.quantity : 0
        return sum + (mesa.impuestosIncluded ? line : line * (1 + (mesa.taxRate ?? 18) / 100))
      }, 0)

      navigate('/tip-selection', {
        state: {
          ticketId,
          mesaId: mesa.mesaId,
          restaurantId: mesa.restaurantId,
          waiterName: mesa.numeroMesa || 'tu mesero',
          amount: subtotal,
          selectedItems: itemsToPay,
          splitCount: paymentMode === PaymentMode.SPLIT_EQUALLY ? splitCount : undefined,
          totalAtTable: paymentMode === PaymentMode.SPLIT_EQUALLY ? totalAtTable : undefined,
          fixedAmount: paymentMode === PaymentMode.FIXED_AMOUNT ? fixedAmount : undefined,
          paymentMode
        }
      })
    } catch (error: any) {
      console.error('Payment error:', error)
      alert(error.message || 'Error al procesar el pago')
      setIsProcessing(false)
    }
  }

  // If session is closed or fully paid from YAP's perspective, show success screen
  if (isClosed || remainingAmount <= 0) {
    return <PaymentSuccessScreen />
  }

  const isFullOnlyMode = initialPaymentMode === 'full'
  const isSelectOnlyMode = initialPaymentMode === 'select'
  const isSplitOnlyMode = initialPaymentMode === 'split'
  const isFixedOnlyMode = initialPaymentMode === 'fixed'
  const singleModeLayout = isFullOnlyMode || isSelectOnlyMode || isSplitOnlyMode || isFixedOnlyMode
  const headerTitle = isFullOnlyMode
    ? 'Pagar cuenta completa'
    : isSelectOnlyMode
      ? 'Pagar por tus items'
      : isSplitOnlyMode
        ? 'Dividir equitativamente'
        : isFixedOnlyMode
          ? 'Pagar una cantidad fija'
        : `Mesa ${mesa.numeroMesa}`
  const hideBillDetails = isSplitOnlyMode || isFixedOnlyMode

  return (
    <div className={`min-h-screen bg-[#F2F2F7] w-full animate-in fade-in slide-in-from-bottom-4 duration-300 ${singleModeLayout ? 'flex flex-col' : ''}`}>
      <Header title={headerTitle} large={singleModeLayout} onBack={singleModeLayout ? onBack : undefined} />

      <main className={`${singleModeLayout ? 'w-full px-4' : 'max-w-md px-4'} mx-auto space-y-3 ${singleModeLayout ? 'py-3 flex-1' : 'py-3'}`}>
        {/* Payment Mode Selector — hidden when user came from landing (full or select) */}
        {!singleModeLayout && (
          <TabSelector 
            currentMode={paymentMode} 
            onModeChange={(mode) => {
              setPaymentMode(mode)
              setSelectedItemIds(new Set())
            }} 
          />
        )}

        {/* Dynamic Controls based on mode (Split Equal shows on top) */}
        {paymentMode === PaymentMode.SPLIT_EQUALLY && (
          <div className="animate-in fade-in slide-in-from-top-4 duration-500">
            <SplitEqualControl
              totalAmount={remainingAmount}
              splitCount={splitCount}
              onCountChange={(n) => setSplitCount(Math.min(totalAtTable, Math.max(1, n)))}
              totalAtTable={totalAtTable}
              onTotalAtTableChange={(n) => {
                const next = Math.max(1, n)
                setTotalAtTable(next)
                if (splitCount > next) setSplitCount(next)
              }}
              numeroMesa={mesa.numeroMesa}
              moneda={moneda}
            />
          </div>
        )}

        {paymentMode === PaymentMode.FIXED_AMOUNT && (
          <div className="animate-in fade-in slide-in-from-top-4 duration-500">
            <ManualFixedAmountControl
              remainingAmount={remainingAmount}
              fixedAmount={fixedAmount}
              onFixedAmountChange={(n) => setFixedAmount(Math.min(Math.max(n, 0), remainingAmount))}
              numeroMesa={mesa.numeroMesa}
              moneda={moneda}
            />
          </div>
        )}

        {/* Bill Details Card — hidden on Dividir equitativamente (view items via Pagar cuenta completa) */}
        {!hideBillDetails && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100/50 overflow-hidden transition-all duration-300">
            {!singleModeLayout && <SearchBar value={searchQuery} onChange={setSearchQuery} />}

            <div className={singleModeLayout ? 'divide-y divide-gray-100' : 'divide-y divide-gray-100/50'}>
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    isSelected={selectedItemIds.has(item.id)}
                    mode={paymentMode}
                    onToggle={handleToggleItem}
                    isPaid={isItemPaid(item)}
                    moneda={moneda}
                    taxRate={taxRate}
                    impuestosIncluded={impuestosIncluded}
                    showItemTax={paymentMode === PaymentMode.SPLIT_EQUALLY}
                    spacious={singleModeLayout}
                  />
                ))
              ) : (
                <div className="p-8 text-center">
                  <p className="text-gray-400 font-medium text-[12px]">No se encontraron items</p>
                </div>
              )}
            </div>

            {/* Subtle total at bottom of list */}
            <div className="h-3 bg-gray-50/30" />
          </section>
        )}

        {/* Summary Card */}
        {paymentMode !== PaymentMode.SPLIT_EQUALLY && paymentMode !== PaymentMode.FIXED_AMOUNT && (
          <SummarySection
            subtotal={billSubtotal}
            taxAmount={billTaxAmount}
            total={totalAmount}
            paid={paidAmount}
            remaining={remainingAmount}
            selectedSubtotal={selectedSubtotal}
            selectedTaxAmount={selectedTaxAmount}
            selectedTotal={currentPayableAmount}
            showSelectionTotal={paymentMode === PaymentMode.SELECT_ITEMS}
            moneda={moneda}
            showTaxBreakdown={!impuestosIncluded}
            hideRestante={singleModeLayout}
            totalLabelLeft={singleModeLayout}
            largeText={singleModeLayout}
          />
        )}

        {/* Full-only or Select-only: Confirmar button; powered by in footer below */}
        {singleModeLayout && (
          <div className="w-full pt-4">
            <button
              onClick={handlePayment}
              disabled={currentPayableAmount <= 0 || isProcessing}
              className={`w-full bg-black text-white py-5 px-6 rounded-xl flex justify-between items-center font-medium text-[15px] transition-all active:scale-[0.98] hover:bg-gray-800 ${currentPayableAmount <= 0 || isProcessing ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {isProcessing ? (
                <div className="w-full flex justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2.5">
                    <Lock size={18} className="text-white flex-shrink-0" />
                    <span>Confirmar</span>
                  </div>
                  <ArrowRight size={20} className="text-white flex-shrink-0" />
                </>
              )}
            </button>
          </div>
        )}
      </main>

      {/* Full-only / Select-only: powered by at full bottom of screen */}
      {singleModeLayout && (
        <footer className="pt-4 pb-8 flex items-center justify-center gap-2 bg-[#F2F2F7]">
          <span className="text-[11px] text-[#8E8E93]">powered by</span>
          <img src={PrimaryLogo} alt="YAP" className="h-10 w-auto object-contain" />
        </footer>
      )}

      {/* Bottom Bar — fixed only when NOT single-mode (full or select) */}
      {!singleModeLayout && (
        <div className="fixed bottom-0 left-0 right-0 p-3 pb-4 z-40 pointer-events-none">
          <div className="max-w-md mx-auto pointer-events-auto">
            <button
              onClick={handlePayment}
              disabled={currentPayableAmount <= 0 || isProcessing}
              className={`
              w-full relative h-14 rounded-xl font-semibold shadow-xl shadow-gray-900/15
              flex items-center justify-between px-4 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]
              overflow-hidden group
              ${currentPayableAmount > 0 && !isProcessing
                ? 'bg-[#1C1C1E] text-white hover:scale-[1.01] active:scale-[0.98]' 
                : 'bg-[#E5E5EA] text-gray-400 cursor-not-allowed shadow-none'}
            `}
            >
              <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] ${currentPayableAmount <= 0 ? 'hidden' : ''}`} />

              {isProcessing ? (
                <div className="w-full flex justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2.5">
                    <div className="bg-white/10 p-1 rounded-lg">
                      <Lock size={14} className={currentPayableAmount > 0 ? "text-white" : "text-gray-400"} />
                    </div>
                    <span className="text-[13px] font-medium tracking-wide">
                      Pagar <span className="opacity-90">{paymentMode === PaymentMode.FULL ? 'Cuenta Completa' : 'Monto'}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="text-[15px] font-bold tracking-tight">
                      {formatCurrency(currentPayableAmount, moneda)}
                    </span>
                    <ArrowRight size={16} className="opacity-60 group-hover:translate-x-1 transition-transform" />
                  </div>
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Global CSS for shimmer and split-from-top */}
      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
        @keyframes slide-in-from-top-4 {
          from { opacity: 0; transform: translateY(-1rem); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-in.slide-in-from-top-4 {
          animation: slide-in-from-top-4 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  )
}
